import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Contact.css';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
// Public visitors get the same top navbar as the About Us page (the landing Nav).
import { Nav, RegisterHelpModal } from './KineticLandingPage';
import Footer from './Footer';


/* ─── Inline SVG Icons ─────────────────────────────────────── */
const ContactIcon = {
  Email: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  Send: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/>
      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  ),
  CheckCircle: () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
};

/* ─── Contact data ────────────────────────────────────────── */
const SUPPORT_EMAIL = 'support@example.com';

/* ─── Contact Page ───────────────────────────────────────── */
const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', organization: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});

  // ── Public-page navbar wiring (same behaviour as AboutUs) ──────────────────
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const [registerHelpOpen, setRegisterHelpOpen] = useState(false);

  // The section tabs live on the landing page, so they go there and the landing
  // page scrolls to the section (it reads `state.scrollTo`).
  const handleNavClick = useCallback((id) => {
    if (id === 'top') { navigate('/'); return; }
    if (id === 'get-started') { navigate('/login'); return; }
    if (id === 'about-us') { navigate('/about-us'); return; }
    if (id === 'contact') {
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate('/', { state: { scrollTo: id } });
  }, [navigate]);

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = 'Name is required';
    if (!form.email.trim())   e.email   = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.subject.trim()) e.subject = 'Subject is required';
    if (!form.message.trim()) e.message = 'Message is required';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSending(true);
    setTimeout(() => { setSending(false); setSubmitted(true); }, 1400);
  };
  
  // Signed-in users see the page inside the app shell (Sidebar + app Navbar);
  // visitors who are not signed in see it as a public page with the public
  // Navbar, so http://localhost:3000/contact works for everyone.
  const isLoggedIn = !!localStorage.getItem('token');

  const content = (
    <div className="ct-page">
      {/* ── Page header ── */}
      <div className="ct-hero">
        <div className="ct-hero-glow" />
        <div className="ct-hero-inner">
          <span className="ct-hero-chip">CONTACT &amp; SUPPORT</span>
          <h1 className="ct-hero-title">Get in <span className="ct-hero-accent">Touch</span></h1>
          <p className="ct-hero-sub">
            Have questions, feedback, or need technical support? Our team is ready to help
            you get the most from the KinetiQ platform.
          </p>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="ct-hero-email">
            <ContactIcon.Email />
            <span>{SUPPORT_EMAIL}</span>
          </a>
        </div>
      </div>

      <div className="ct-body">
        {/* Contact information cards intentionally omitted per product feedback. */}
        {/* ── Main two-column section ── */}
        <div className="ct-main-grid">
          {/* ── Contact form ── */}
          <section className="ct-form-section">
            <div className="ct-section-head">
              <span className="ct-section-chip">DIRECT MESSAGE</span>
              <h2 className="ct-section-title">Send Us a Message</h2>
              <p className="ct-section-desc">Fill in the form and we'll get back to you within one business day.</p>
            </div>

            {submitted ? (
              <div className="ct-success">
                <div className="ct-success-icon">
                  <ContactIcon.CheckCircle />
                </div>
                <h3 className="ct-success-title">Message Sent!</h3>
                <p className="ct-success-msg">
                  Thanks, <strong>{form.name}</strong>. We've received your message and will reply to <strong>{form.email}</strong> shortly.
                </p>
                <button className="ct-success-btn" onClick={() => { setSubmitted(false); setForm({ name: '', email: '', organization: '', subject: '', message: '' }); }}>
                  Send Another
                </button>
              </div>
            ) : (
              <form className="ct-form" onSubmit={handleSubmit} noValidate>
                <div className="ct-form-row">
                  <div className={`ct-field ${errors.name ? 'ct-field--error' : ''}`}>
                    <label className="ct-label" htmlFor="ct-name">Full Name</label>
                    <input
                      id="ct-name"
                      className="ct-input"
                      type="text"
                      name="name"
                      placeholder="Full Name"
                      value={form.name}
                      onChange={handleChange}
                      autoComplete="name"
                    />
                    {errors.name && <span className="ct-error-msg">{errors.name}</span>}
                  </div>
                  <div className={`ct-field ${errors.email ? 'ct-field--error' : ''}`}>
                    <label className="ct-label" htmlFor="ct-email">Email Address</label>
                    <input
                      id="ct-email"
                      className="ct-input"
                      type="email"
                      name="email"
                      placeholder="name@example.com"
                      value={form.email}
                      onChange={handleChange}
                      autoComplete="email"
                    />
                    {errors.email && <span className="ct-error-msg">{errors.email}</span>}
                  </div>
                </div>

                <div className="ct-form-row">
                  <div className="ct-field">
                    <label className="ct-label" htmlFor="ct-organization">
                      Organization / Institution <span className="ct-label-optional">(optional)</span>
                    </label>
                    <input
                      id="ct-organization"
                      className="ct-input"
                      type="text"
                      name="organization"
                      placeholder="Company or institution name"
                      value={form.organization}
                      onChange={handleChange}
                      autoComplete="organization"
                    />
                  </div>

                  <div className={`ct-field ${errors.subject ? 'ct-field--error' : ''}`}>
                    <label className="ct-label" htmlFor="ct-subject">Subject</label>
                    <input
                      id="ct-subject"
                      className="ct-input"
                      type="text"
                      name="subject"
                      placeholder="How can we help you?"
                      value={form.subject}
                      onChange={handleChange}
                    />
                    {errors.subject && <span className="ct-error-msg">{errors.subject}</span>}
                  </div>
                </div>

                <div className={`ct-field ${errors.message ? 'ct-field--error' : ''}`}>
                  <label className="ct-label" htmlFor="ct-message">Message</label>
                  <textarea
                    id="ct-message"
                    className="ct-textarea"
                    name="message"
                    rows={5}
                    placeholder="Describe your question or issue in detail…"
                    value={form.message}
                    onChange={handleChange}
                  />
                  {errors.message && <span className="ct-error-msg">{errors.message}</span>}
                </div>

                <button className={`ct-submit-btn ${sending ? 'ct-submit-btn--sending' : ''}`} type="submit" disabled={sending}>
                  {sending ? (
                    <span className="ct-spinner" />
                  ) : (
                    <>
                      <ContactIcon.Send />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </section>
        </div>
      </div>
    </div>
  ); // end ct-page

  if (!isLoggedIn) {
    return (
      <div
        ref={scrollRef}
        style={{ height: '100vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}
      >
        <Nav
          scrollContainerRef={scrollRef}
          onNavClick={handleNavClick}
          onOpenRegisterHelp={() => setRegisterHelpOpen(true)}
          currentPageId="contact"
        />
        <div style={{ flex: 1 }}>{content}</div>
        <Footer />

        <RegisterHelpModal
          open={registerHelpOpen}
          onClose={() => setRegisterHelpOpen(false)}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' }}>

      <Sidebar />

      <div style={{
        marginLeft:    '220px',
        width:         'calc(100% - 220px)',
        display:       'flex',
        flexDirection: 'column',
        height:        '100vh',
        overflow:      'hidden',
      }}>

        <Navbar activePage="app" />

        <div style={{
          flex:      1,
          marginTop: '68px',
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          display:   'flex',
          flexDirection: 'column',
        }}>
          {content}
          <Footer />
        </div>   {/* end scrollable content */}
      </div>     {/* end rightCol */}
    </div>
  );
};

export default Contact;